import { effect, inject, Injectable } from "@angular/core";
import { PaloggetbrukerService } from "@husbanken/bruker-api";
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from "keycloak-angular";
import { UserRepresentsStore } from "../stores/user-represents.store";
import { UserStore } from "../stores/user.store";
import { firstValueFrom } from "rxjs";
import { UserQuery } from "../queries/user.query";
import Keycloak, { KeycloakConfig } from "keycloak-js";
import { Environment } from "../models/environment.model";
import { TestService } from "./test.service";
import _keycloakConfig from "../../../konfig/keycloak-privat.json";
import _keycloakConfigProff from "../../../konfig/keycloak-proff.json";
import _keycloakConfigLocalSso from "../../../konfig/keycloak-local-sso.json";
import { AppConfigService } from "./app-config.service";

@Injectable({providedIn: "root"})
export class UserService {
  private paloggetBrukerService = inject(PaloggetbrukerService);
  private userQuery = inject(UserQuery);
  private userStore = inject(UserStore);
  private testService = inject(TestService);
  private userRepresentsStore = inject(UserRepresentsStore);
  private keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL, {optional: true});
  private keycloak = inject(Keycloak, {optional: true});
  private appConfigService = inject(AppConfigService);

  constructor() {
    if (TestService.isRunningInFrontendTestContext) {
      this.testService.loginFrontendTest();
    } else {
      this.registerLoginEventHandler();
    }
  }

  public async registerLoginEventHandler(): Promise<void> {
    effect(async () => {
      const keycloakEvent = this.keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        const authenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);

        if (!authenticated) {
          return;
        }

        this.userStore.update({...this.keycloak.tokenParsed, realm: this.keycloak.realm});

        if (this.keycloak.realm.includes("proff")) {
          const brukerensOrganisasjoner = await firstValueFrom(this.paloggetBrukerService.hentPaloggetbrukersOrganisasjoner());
          if (brukerensOrganisasjoner?.length) {
            this.userRepresentsStore.add(brukerensOrganisasjoner);
            this.userRepresentsStore.setActive(this.userQuery.orgnr);
          }
        }
      }
    });
  }

  public async logout(): Promise<void> {
    await this.keycloak.logout({redirectUri: this.appConfigService.appConfig()?.logoutRedirectUri});
    this.userRepresentsStore.reset();
    this.userStore.reset();
  }

  public static async fetchKeycloakConfig(environment: Environment): Promise<KeycloakConfig> {
    const urlRealm = window.location.pathname.split('/').filter(segment => segment !== '')[0];
    const proffRealm = 'proff';
    const privatRealm = 'privat';


    let keycloakUrl = environment.keycloakConfig;
    if (!environment.production) {
      if (keycloakUrl.includes("sso")) {
        return _keycloakConfigLocalSso as KeycloakConfig;
      }

      if (urlRealm === privatRealm) {
        return _keycloakConfig as KeycloakConfig;
      } else if (urlRealm === proffRealm) {
        return _keycloakConfigProff as KeycloakConfig
      } else { // Redirect to 'realm-picker' page (without creating an entry in the history stack)
        window.location.replace('https://husbanken.no/person/logg-inn/');
      }

      return _keycloakConfig as KeycloakConfig;
    }

    try {
      const realmRegex = /\[realm]/;
      if (urlRealm === privatRealm) {
        keycloakUrl = keycloakUrl.replace(realmRegex, privatRealm); // konfig/keycloak-privat.json
      } else if (urlRealm === proffRealm) {
        keycloakUrl = keycloakUrl.replace(realmRegex, proffRealm); // konfig/keycloak-proff.json
      } else { // Redirect to 'realm-picker' page (without creating an entry in the history stack)
        window.location.replace('https://husbanken.no/person/logg-inn/');
      }
      return await (await fetch(keycloakUrl)).json();
    } catch (error) {
      throw new Error(`Kunne ikke hente keycloak config fra ${keycloakUrl}. Returnerer default keycloak config.`, {cause: error});
    }
  }
}
